import type { VmtNarrativeInput } from './vmtNarrative';
import { buildVmtRecoverySentence } from '../../utils/transportationNarrativeHelpers';
import {
  canCompareMetrics,
  VMT_LDV_PER_CAPITA_FACTS,
} from './metricComparator';
import { compareToReference, isFiniteNumber } from './narrativeFormatting';
import { buildTrendSentence } from './optionalClauseTokens';
import type { NarrativeTokenMap } from './narrativeTemplateEngine';

const VMT_TREND_BASELINE_YEAR = 2019;

export function buildVmtNarrativeTokens(input: VmtNarrativeInput): NarrativeTokenMap {
  const loc = input.locationName;
  const recoverySentence = buildVmtRecoverySentence(
    input.annualLdvSeries.map((d) => d.year),
    input.annualLdvSeries.map((d) => d.vmt),
    VMT_TREND_BASELINE_YEAR
  );

  if (input.viewId === 'total') {
    const chg = input.changeSince2019AnnualPct;
    return {
      locationName: loc,
      reportingYear: input.reportingYear,
      annualLdvVmt: isFiniteNumber(input.annualLdvVmt)
        ? Math.round(input.annualLdvVmt).toLocaleString()
        : null,
      trendSentence: buildTrendSentence(chg, VMT_TREND_BASELINE_YEAR),
      trendPhrase: '',
      recoverySentence,
    };
  }

  if (input.viewId === 'comparison') {
    return { locationName: loc, reportingYear: input.reportingYear };
  }

  const chg = input.changeSince2019Pct;
  const ca = input.californiaPerCapitaMiPerDay;
  const compat =
    isFiniteNumber(input.perCapitaMiPerDay) && isFiniteNumber(ca)
      ? canCompareMetrics(
          VMT_LDV_PER_CAPITA_FACTS(input.reportingYear),
          { ...VMT_LDV_PER_CAPITA_FACTS(input.reportingYear), geographyLevel: 'state' }
        )
      : { compatible: false, reasons: [] as string[] };
  const cmp = compareToReference(input.perCapitaMiPerDay, ca);
  const californiaComparisonSentence =
    compat.compatible && cmp.relation !== 'unavailable' && isFiniteNumber(ca)
      ? ` Compared with California’s ${ca} miles per resident per day in ${input.reportingYear}.`
      : '';

  return {
    locationName: loc,
    reportingYear: input.reportingYear,
    perCapitaMiPerDay: input.perCapitaMiPerDay,
    trendSentence: buildTrendSentence(chg, VMT_TREND_BASELINE_YEAR),
    trendPhrase: '',
    californiaComparisonSentence,
    recoverySentence,
  };
}
